import type { PlaybackLayer } from 'components/Playback/types';
import {
  isMapPointOnHighlightedSurvey,
  type HighlightedSurveyDropCache,
  type MapPointLike,
} from './surveyDropTarget';
import {
  resolveNearestImageIndex,
  resolveSurveyPlaybackSelection,
  type PlaybackWfsLayerSelection,
  type SurveyPlaybackPanelLayer,
  type SurveyPlaybackSelectionDecision,
} from './surveyPlaybackHelpers';

export type PegmanPanelLayer = SurveyPlaybackPanelLayer;

export type PegmanDropGeometrySources = {
  readonly mapSurveySlugGeometries: readonly unknown[];
  readonly panelWfsFallbackGeometries: readonly unknown[];
};

/**
 * Builds the drop-target cache used during pegman drag
 * Prefers map `survey_slug` query hits; falls back to panel WFS geometries
 * (needed when only WMTS ortho is on the map)
 */
export function resolvePegmanDropGeometryCache(
  surveySlug: string,
  sources: PegmanDropGeometrySources,
): HighlightedSurveyDropCache | null {
  if (sources.mapSurveySlugGeometries.length > 0) {
    return {
      slug: surveySlug,
      geometries: sources.mapSurveySlugGeometries,
    };
  }
  if (sources.panelWfsFallbackGeometries.length > 0) {
    return {
      slug: surveySlug,
      geometries: sources.panelWfsFallbackGeometries,
    };
  }
  return null;
}

export type PegmanDropSelectionDecision = SurveyPlaybackSelectionDecision;

export function resolvePegmanDropSelection(
  panelLayers: ReadonlyArray<PegmanPanelLayer>,
  maxSimultaneousImages: number,
): PegmanDropSelectionDecision {
  return resolveSurveyPlaybackSelection(panelLayers, maxSimultaneousImages);
}

export type PegmanNearestImageDecision =
  | { readonly outcome: 'no-nearest-image' }
  | {
      readonly outcome: 'ok';
      readonly nearestImageIndex: number;
      readonly nearestSpatialIndex: number | null;
      readonly nearestLayerKey: string | null;
      readonly nearestPoint: {
        readonly x: number;
        readonly y: number;
        readonly spatialReference?: { readonly wkid?: number };
      };
      readonly distanceMeters: number;
    };

/**
 * After a corridor-validated drop, snap to the nearest playback image
 * Does not re-apply the zoom-scaled drop radius — image spacing is often wider
 */
export function resolvePegmanNearestImageForPlayback(
  loadedPlaybackLayers: readonly PlaybackLayer[],
  dropPoint: MapPointLike,
): PegmanNearestImageDecision {
  if (loadedPlaybackLayers.length === 0) {
    return { outcome: 'no-nearest-image' };
  }

  const nearest = resolveNearestImageIndex(loadedPlaybackLayers, dropPoint);
  if (
    nearest.imageIndex === null ||
    nearest.point == null ||
    typeof nearest.point.x !== 'number' ||
    typeof nearest.point.y !== 'number'
  ) {
    return { outcome: 'no-nearest-image' };
  }

  return {
    outcome: 'ok',
    nearestImageIndex: nearest.imageIndex,
    nearestSpatialIndex: nearest.spatialIndex,
    nearestLayerKey: nearest.layerKey,
    nearestPoint: {
      x: nearest.point.x,
      y: nearest.point.y,
      spatialReference: nearest.point.spatialReference,
    },
    distanceMeters: nearest.distanceMeters,
  };
}

export type PegmanDropPlaybackDecision =
  | { readonly outcome: 'not-on-survey' }
  | { readonly outcome: 'no-wfs-layers' }
  | {
      readonly outcome: 'capacity-limit';
      readonly selection: PlaybackWfsLayerSelection;
    }
  | { readonly outcome: 'no-playback-features' }
  | { readonly outcome: 'no-nearest-image' }
  | {
      readonly outcome: 'start-playback';
      readonly selection: PlaybackWfsLayerSelection;
      readonly nearestImageIndex: number;
      readonly nearestSpatialIndex: number | null;
      readonly nearestLayerKey: string | null;
      readonly nearestPoint: {
        readonly x: number;
        readonly y: number;
        readonly spatialReference?: { readonly wkid?: number };
      };
      readonly distanceMeters: number;
    };

export type ResolvePegmanDropPlaybackInput = {
  readonly surveySlug: string;
  readonly panelLayers: ReadonlyArray<PegmanPanelLayer>;
  readonly maxSimultaneousImages: number;
  readonly dropPoint: MapPointLike;
  readonly dropCache: HighlightedSurveyDropCache | null;
  readonly measureDistanceMeters: (
    from: MapPointLike,
    geometry: unknown,
  ) => number | null;
  readonly maxDropDistanceMeters: number;
  readonly loadedPlaybackLayers: readonly PlaybackLayer[];
};

/**
 * End-to-end pegman drop -> playback decision 
 *
 * Order:
 * 1. Corridor hit-test against highlighted drop geometries
 * 2. WFS layer selection (+ capacity only for explicit over-check)
 * 3. Require loaded playback features
 * 4. Snap to nearest image — no second hard distance abort after corridor accept
 */
export function resolvePegmanDropPlayback(
  input: ResolvePegmanDropPlaybackInput,
): PegmanDropPlaybackDecision {
  const onSurvey = isMapPointOnHighlightedSurvey(
    input.dropPoint,
    input.surveySlug,
    input.dropCache,
    input.measureDistanceMeters,
    input.maxDropDistanceMeters,
  );
  if (!onSurvey) {
    return { outcome: 'not-on-survey' };
  }

  const selectionDecision = resolvePegmanDropSelection(
    input.panelLayers,
    input.maxSimultaneousImages,
  );
  if (selectionDecision.outcome === 'no-wfs-layers') {
    return { outcome: 'no-wfs-layers' };
  }
  if (selectionDecision.outcome === 'capacity-limit') {
    return {
      outcome: 'capacity-limit',
      selection: selectionDecision.selection,
    };
  }

  if (input.loadedPlaybackLayers.length === 0) {
    return { outcome: 'no-playback-features' };
  }

  const nearestDecision = resolvePegmanNearestImageForPlayback(
    input.loadedPlaybackLayers,
    input.dropPoint,
  );
  if (nearestDecision.outcome === 'no-nearest-image') {
    return { outcome: 'no-nearest-image' };
  }

  return {
    outcome: 'start-playback',
    selection: selectionDecision.selection,
    nearestImageIndex: nearestDecision.nearestImageIndex,
    nearestSpatialIndex: nearestDecision.nearestSpatialIndex,
    nearestLayerKey: nearestDecision.nearestLayerKey,
    nearestPoint: nearestDecision.nearestPoint,
    distanceMeters: nearestDecision.distanceMeters,
  };
}
